/**
 * Configuration Hook - RTK Query Wrapper
 * 
 * This hook provides a simple interface for consuming configuration data,
 * powered by Redux Toolkit Query which handles:
 * - Automatic request deduplication
 * - Built-in caching with 1-hour TTL
 * - Background refetching when data is stale
 * - Loading and error states
 * - Failure tracking with exponential backoff
 * - Redirect to error page for critical auth config failures
 * 
 * Replaces the complex custom caching logic with battle-tested RTK Query.
 * 
 * Usage:
 * const { config, loading, error, refresh, isStale, failed } = useConfig('sign-in-config');
 */

import { useCallback, useEffect, useRef } from 'react';
import { useGetConfigQuery, useRefreshConfigMutation } from '../store/configApi';
import { ConfigValues } from './configManager';
import {
    CRITICAL_AUTH_CONFIGS,
    recordFailure,
    clearFailure,
    isInBackoff
} from './configHook/failure';

/**
 * Check if we're currently on the config error page
 */
const isOnConfigErrorPage = (): boolean => {
    try {
        return globalThis.location?.pathname === '/config-error';
    } catch {
        return false;
    }
};

export interface UseConfigReturn {
    /** The configuration values */
    config: ConfigValues;
    /** Whether the hook is currently loading/refreshing */
    loading: boolean;
    /** Any error that occurred during fetch */
    error: string | null;
    /** Function to manually refresh the configuration */
    refresh: () => Promise<void>;
    /** Whether the current config is stale (older than 1 hour) */
    isStale: boolean;
    /** Whether the configuration fetch failed */
    failed: boolean;
}

/**
 * Type guard to check if a value is a ConfigValues object
 */
export const isConfigObject = (value: string | string[] | number | boolean | ConfigValues): value is ConfigValues => {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
};

/**
 * Safely get a nested property from ConfigValues
 * @param config - The config object
 * @param path - The path to the property (e.g., 'register.nameservers.custom')
 * @returns The value at the path or undefined if not found
 */
export const getNestedConfig = (config: ConfigValues, path: string): string | string[] | number | boolean | ConfigValues | ConfigValues[] | undefined => {
    const keys = path.split('.');
    let current: string | string[] | number | boolean | ConfigValues | ConfigValues[] = config;

    for (const key of keys) {
        if (Array.isArray(current) || !isConfigObject(current) || !(key in current)) {
            return undefined;
        }

        current = current[key];
    }

    return current;
};

/**
 * Safely get a nested boolean property from ConfigValues
 * @param config - The config object
 * @param path - The path to the property (e.g., 'register.nameservers.custom')
 * @param defaultValue - Default value if not found or not boolean
 * @returns The boolean value or default
 */
export const getNestedBoolean = (config: ConfigValues, path: string, defaultValue = false): boolean => {
    const value = getNestedConfig(config, path);
    return typeof value === 'boolean' ? value : defaultValue;
};

/**
 * Safely get a nested string property from ConfigValues
 * @param config - The config object
 * @param path - The path to the property
 * @param defaultValue - Default value if not found or not string
 * @returns The string value or default
 */
export const getNestedString = (config: ConfigValues, path: string, defaultValue = ''): string => {
    const value = getNestedConfig(config, path);
    return typeof value === 'string' ? value : defaultValue;
};

/**
 * Safely get a nested number property from ConfigValues
 * @param config - The config object
 * @param path - The path to the property
 * @param defaultValue - Default value if not found or not number
 * @returns The number value or default
 */
export const getNestedNumber = (config: ConfigValues, path: string, defaultValue = 0): number => {
    const value = getNestedConfig(config, path);
    return typeof value === 'number' ? value : defaultValue;
};

/**
 * Safely get a nested object property from ConfigValues
 * @param config - The config object
 * @param path - The path to the property
 * @param defaultValue - Default value if not found or not object
 * @returns The object value or default
 */
export const getNestedObject = (config: ConfigValues, path: string, defaultValue: ConfigValues = {}): ConfigValues => {
    const value = getNestedConfig(config, path);
    return !Array.isArray(value) && isConfigObject(value) ? value : defaultValue;
};

/**
 * Safely get a nested array property from ConfigValues
 * @param config - The config object
 * @param path - The path to the property
 * @param defaultValue - Default value if not found or not array
 * @returns The array value or default
 */
export const getNestedArray = <T = string | number | boolean | ConfigValues>(config: ConfigValues, path: string, defaultValue: T[] = []): T[] => {
    const value = getNestedConfig(config, path);
    return Array.isArray(value) ? (value as T[]) : defaultValue;
};

/**
 * Custom hook for managing configuration with RTK Query
 * @param configName - The name of the configuration to manage
 * @param enabled - Whether to fetch the configuration (default: true). Set to false to skip fetching.
 * @returns Configuration state and management functions
 */
export const useConfig = (configName: string, enabled = true): UseConfigReturn => {
    // Validate config name - prevent empty/undefined/null config names
    const isValidConfigName = configName && configName.trim() !== '';

    // Check if we're on the config error page - skip critical configs to prevent loop
    const onConfigErrorPage = isOnConfigErrorPage();
    const isCriticalConfig = CRITICAL_AUTH_CONFIGS.includes(configName);

    // Check if we're in backoff period or already redirecting
    const inBackoff = isInBackoff(configName);
    const shouldSkip = !enabled || !isValidConfigName || (onConfigErrorPage && isCriticalConfig) || inBackoff;

    // Track previous error state to detect new failures
    const prevErrorRef = useRef<boolean>(false);
    const prevSuccessRef = useRef<boolean>(false);

    // Use RTK Query hook to fetch config
    const {
        data: configData,
        isLoading,
        isFetching,
        error: queryError,
        refetch
    } = useGetConfigQuery(configName, {
        skip: shouldSkip,
        // Don't refetch on mount if we already have data - sessionStorage will be checked in queryFn
        refetchOnMountOrArgChange: false
    });

    // Mutation for manual refresh
    const [refreshConfigMutation] = useRefreshConfigMutation();

    // Extract config values or use empty object
    const config = configData?.config_value || {};

    // Determine if there's an error
    const getErrorMessage = (): string | null => {
        if (!isValidConfigName) return 'Invalid config name provided';

        if (inBackoff) return 'Config in backoff period';

        if (queryError) {
            return 'status' in queryError
                ? `Failed to fetch configuration: ${queryError.status}`
                : 'Failed to fetch configuration';
        }

        return null;
    };
    const error = getErrorMessage();

    // Failed state - true if there's an error (including invalid config name)
    const failed = !isValidConfigName || !!queryError || inBackoff;

    // Loading state - true during initial load or refetch
    const loading = isLoading || isFetching;

    // Track failures for backoff and redirect logic
    useEffect(() => {
        if (!isValidConfigName || isOnConfigErrorPage()) return;

        const hasError = !!queryError;
        const hasData = !!configData;

        // Record failure when we transition to error state
        if (hasError && !prevErrorRef.current && !loading) {
            recordFailure(configName, queryError);
        }

        // Clear failure when we get successful data
        if (hasData && !hasError && !prevSuccessRef.current) {
            clearFailure(configName);
        }

        prevErrorRef.current = hasError;
        prevSuccessRef.current = hasData && !hasError;
    }, [configName, queryError, configData, loading, isValidConfigName]);

    // Check if data is stale (RTK Query handles this automatically, but we expose it for compatibility)
    // Data is considered stale after 1 hour based on keepUnusedDataFor setting
    const isStale = false; // RTK Query automatically handles staleness

    // Track the latest refetch in a ref so `refresh` below can stay
    // referentially stable without listing `refetch` as a dependency.
    const refetchRef = useRef(refetch);
    refetchRef.current = refetch;

    // Manual refresh function.
    //
    // Wrapped in useCallback so that `refresh` is referentially stable across
    // renders. Consumers that list `refresh` as a `useEffect` dependency
    // (TermsLink / PrivacyLink) will then run it exactly once on mount.
    // An unstable `refresh` re-ran those effects on every render, triggering
    // a self-perpetuating refresh loop that exhausted the browser's per-host
    // socket pool (net::ERR_INSUFFICIENT_RESOURCES).
    const refresh = useCallback(async (): Promise<void> => {
        // Validate config name before attempting refresh
        if (!isValidConfigName || isOnConfigErrorPage()) {
            console.warn('Cannot refresh: invalid config name or on config error page');
            return;
        }

        try {
            await refreshConfigMutation(configName).unwrap();
            clearFailure(configName);
            // Optionally refetch to update the query cache
            refetchRef.current();
        } catch (error_) {
            console.warn('Config refresh failed:', error_);
            recordFailure(configName, error_);
        }
    }, [configName, isValidConfigName, refreshConfigMutation]);

    return {
        config,
        loading,
        error,
        refresh,
        isStale,
        failed
    };
};
